import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Logo from '../images/lavenir-logo.png';
import { loadStripe } from '@stripe/stripe-js';
import { stripeApi } from '../apis/stripe.api';
import { getCurrentRegionLabel } from '../utils/regionHelper';
import { Select } from '@mantine/core';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const ExplorerPurchaseNew = ({ onBack }) => {
  
  const [formData, setFormData] = useState({
    seats: 1,
    name: '',
    email: '',
    companyName: '',
    region: process.env.REACT_APP_REGION_CODE?.toLowerCase(),
    db: process.env.REACT_APP_DEPLOY_SERVER,
    paymentSchedule: 'annual',
    acceptedTerms: false
  });

  // Check if form is valid using useMemo to prevent unnecessary recalculations
  const isFormValid = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return formData.name.trim() !== '' && 
           emailRegex.test(formData.email) && 
           formData.acceptedTerms;
  }, [formData.name, formData.email, formData.acceptedTerms]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'seats') {
      const numValue = parseInt(value);
      if (numValue > parseInt(process.env.REACT_APP_EXPLORER_MAX_SEATS)) {
        alert('Explorer plan is limited to 9 seats. For more seats, please use our Enterprise plan.');
        setFormData(prev => ({ ...prev, seats: parseInt(process.env.REACT_APP_EXPLORER_MAX_SEATS) }));
        return;
      }
      setFormData(prev => ({
        ...prev,
        seats: value === '' ? '' : numValue
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission data:', formData);
    
    try {
      const { url } = await stripeApi.createCheckoutSession(formData, 'explorer');
      
      if (!url) {
        throw new Error('No checkout URL received from server');
      }
      window.location.href = url;
    } catch (error) {
      console.error('Submission error details:', error);
      alert('Payment failed. Please try again.');
    }
  };

  // Add regionData calculation like in AddUsers
  const regionData = (process.env.REACT_APP_REGIONS?.split(',') || [])
    .map((code, index) => ({
      value: code.toLowerCase(),
      label: (process.env.REACT_APP_REGION_LABELS?.split(',') || [])[index] || code
    }));

  return (
    <div className="min-h-screen bg-gradient-to-tr from-[#eef2f3] via-[#d9e6f2] to-[#eef2f3] py-6">
      <div className="max-w-[520px] mx-auto">
        <div className="bg-white rounded-[12px] shadow-lg p-7">
          <img src={Logo} alt="LavenirAI" className="h-[38px] mx-auto mb-4" />
          
          <h1 className="text-[#10193A] text-[28px] font-bold text-center mb-6">
            Complete Explorer Purchase
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-600 mb-1">Number of Seats:</label>
              <input
                type="number"
                name="seats"
                min="1"
                max="9"
                value={formData.seats}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
                required
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Company Name: (optional)</label>
              <input
                type="text"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                className="w-full p-1.5 border border-gray-300 rounded-lg"
              />
            </div>

            <div>
              <label className="block text-gray-600 mb-1">Main Region:</label>
              <Select
                value={formData.region}
                onChange={(value) => handleChange({ target: { name: 'region', value } })}
                data={regionData}
                disabled={true}
                styles={{
                  input: { 
                    height: '36px',
                    backgroundColor: '#f8f9fa'
                  },
                  label: {
                    marginBottom: '4px',
                    fontWeight: 500,
                  }
                }}
              />
            </div>

            <div>
              <div className="flex items-center gap-4">
                <label className="text-gray-600">Payment Schedule:</label>
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg bg-[#10193A] text-white"
                >
                  Yearly
                </button>
                {/* Monthly button hidden but kept in code for future use
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg bg-gray-200 text-gray-700"
                >
                  Monthly
                </button>
                */}
              </div>
            </div>

            <div className="bg-gray-100 p-3 rounded-lg">
              {false && <p className="text-gray-600 text-sm">All plans are 1 year. Save $100/seat with Yearly prepaid.</p>}
              {false && <p className="text-2xl font-bold mt-2">${formData.paymentSchedule === 'annual' ? process.env.EXPLORER_PRICE_PER_SEAT_4QTR * formData.seats : process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_MONTHLY * formData.seats}/year</p>}
              <p className="text-2xl font-bold mt-2">${process.env.REACT_APP_EXPLORER_PRICE_PER_SEAT_4QTR * formData.seats}/year</p>
            
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                name="acceptedTerms"
                checked={formData.acceptedTerms}
                onChange={handleChange}
                className="mt-1"
              />
              <label htmlFor="terms" className="text-sm text-gray-600">
                I accept the <a href="https://lavenirai.com/privacy-policy-explorer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>, 
                <a href="https://lavenirai.com/explorer-tos-agreement" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> Subscription Agreement</a>, and 
                <a href="https://lavenirai.com/eula-explorer" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline"> EULA</a>.
              </label>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onBack}
                className="w-1/4 py-2.5 border border-[#10193A] text-[#10193A] rounded-xl"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`w-3/4 rounded-xl py-2.5 ${
                  isFormValid 
                    ? 'bg-[#10193A] text-white' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Buy Now
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ExplorerPurchaseNew;